import { useEffect, useState } from 'react';
import { Button, Select } from 'antd';
import { useAppDispatch, useAppSelector } from '../../store/store';
import {
  getTaskFormBatches,
  selectTaskFormBatches,
} from '../../store/reducers/TaskFormSlice';
import {
  getDispensarizationBatches,
  selectDispensarizationBatches,
} from '../../store/reducers/DispensarizationBatchesSlice';
import {
  getTaskCampaignGroups,
  type TaskCampaignFilters,
} from '../../models/Task/campaignFilters';
import { useFilterOption } from '../../hooks/shared/useFilterOption';
import { FILTER_FIELD_TEXTS } from './filterFieldTexts';
import styles from './filterPanelDefaults.module.css';

interface Props {
  readonly types: readonly string[];
  readonly value: TaskCampaignFilters;
  readonly onChange: (value: TaskCampaignFilters) => void;
  readonly onOpenChange?: (open: boolean) => void;
  readonly disabled?: boolean;
  readonly classNames?: {
    readonly container: string;
    readonly input: string;
    readonly label?: string;
  };
}

export const TaskCampaignFilter = ({
  types,
  value,
  onChange,
  onOpenChange,
  disabled,
  classNames,
}: Props) => {
  const dispatch = useAppDispatch();
  const formBatches = useAppSelector(selectTaskFormBatches);
  const dispensarizationBatches = useAppSelector(selectDispensarizationBatches);
  const { form, dispensarization } = getTaskCampaignGroups(types);
  const filterOption = useFilterOption();
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!form && !dispensarization) return;
    let active = true;
    setLoading(true);
    setFailed(false);
    const requests = [];
    if (form) requests.push(dispatch(getTaskFormBatches()).unwrap());
    if (dispensarization) {
      requests.push(dispatch(getDispensarizationBatches()).unwrap());
    }
    Promise.all(requests).then(
      () => {
        if (active) setLoading(false);
      },
      () => {
        if (active) {
          setLoading(false);
          setFailed(true);
        }
      },
    );
    return () => {
      active = false;
    };
  }, [dispatch, form, dispensarization, retry]);

  if (!form && !dispensarization) return null;

  const options = [
    ...(form
      ? [
          {
            label: 'Анкеты',
            options: formBatches.map((batch) => ({
              value: `form:${batch.id}`,
              label: batch.name,
            })),
          },
        ]
      : []),
    ...(dispensarization
      ? [
          {
            label: 'Диспансеризация',
            options: dispensarizationBatches.map((batch) => ({
              value: `dispensarization:${batch.id}`,
              label: batch.name,
            })),
          },
        ]
      : []),
  ];
  const selected = [
    ...(form ? (value.FormBatchIds ?? []).map((id) => `form:${id}`) : []),
    ...(dispensarization
      ? (value.DispensarizationBatchIds ?? []).map(
          (id) => `dispensarization:${id}`,
        )
      : []),
  ];
  const handleChange = (values: string[]) => {
    onChange({
      FormBatchIds: form
        ? values.filter((id) => id.startsWith('form:')).map((id) => id.slice(5))
        : undefined,
      DispensarizationBatchIds: dispensarization
        ? values
            .filter((id) => id.startsWith('dispensarization:'))
            .map((id) => id.slice(17))
        : undefined,
    });
  };

  return (
    <div className={classNames?.container ?? styles.filterInputsContainer}>
      <label
        className={classNames ? classNames.label : styles.filterLabel}
        htmlFor="task-campaign-filter"
      >
        {FILTER_FIELD_TEXTS.batch.label}
      </label>
      <Select
        id="task-campaign-filter"
        aria-label={FILTER_FIELD_TEXTS.batch.label}
        className={classNames?.input ?? styles.filterInput}
        placeholder={FILTER_FIELD_TEXTS.batch.placeholder}
        mode="multiple"
        allowClear
        showSearch
        maxTagCount="responsive"
        maxTagTextLength={24}
        autoClearSearchValue={false}
        filterOption={filterOption}
        options={options}
        value={selected}
        onChange={handleChange}
        onDropdownVisibleChange={onOpenChange}
        loading={loading}
        disabled={disabled || loading || failed}
      />
      {failed && (
        <div role="alert">
          Не удалось загрузить кампании.{' '}
          <Button
            type="link"
            onClick={() => setRetry((previous) => previous + 1)}
          >
            Повторить
          </Button>
        </div>
      )}
    </div>
  );
};
